import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Scale, UploadCloud, Loader2, CheckCircle2, AlertCircle,
  Trash2, RefreshCw, X, ChevronDown, BarChart3, List,
  TrendingUp, Package, Truck, Pencil, Download, Filter
} from "lucide-react";
import { toast } from "sonner";

const PERIOD_OPTIONS = [
  { id: "week", label: "Esta semana" },
  { id: "month", label: "Este mes" },
  { id: "3months", label: "Últimos 3 meses" },
  { id: "year", label: "Este año" },
  { id: "custom", label: "Personalizado" },
];

function getPeriodDates(period) {
  const today = new Date();
  const fmt = (d) => d.toISOString().split("T")[0];
  if (period === "week") {
    const mon = new Date(today);
    mon.setDate(today.getDate() - today.getDay() + 1);
    return { from: fmt(mon), to: fmt(today) };
  }
  if (period === "month") {
    return { from: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), to: fmt(today) };
  }
  if (period === "3months") {
    const d = new Date(today); d.setMonth(d.getMonth() - 3);
    return { from: fmt(d), to: fmt(today) };
  }
  if (period === "year") {
    return { from: `${today.getFullYear()}-01-01`, to: fmt(today) };
  }
  return { from: "", to: "" };
}

// ── Stat Card ──────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, unit, color = "#007934" }) {
  return (
    <div className="card-white p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: color + "18" }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div>
        <p className="text-xs text-slate-400 font-medium">{label}</p>
        <p className="text-xl font-bold text-slate-800 leading-tight">
          {value !== null && value !== undefined ? value.toLocaleString("es-ES") : "—"}{unit && <span className="text-sm font-normal text-slate-400 ml-1">{unit}</span>}
        </p>
      </div>
    </div>
  );
}

// ── Mini Bar Chart ─────────────────────────────────────────────────────────────
function MiniBarChart({ data, keyX, keyY, color = "#007934", formatY }) {
  if (!data || data.length === 0) return <p className="text-sm text-slate-400 text-center py-8">Sin datos</p>;
  const max = Math.max(...data.map(d => d[keyY]));
  return (
    <div className="flex items-end gap-1 h-28 w-full">
      {data.slice(-20).map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative">
          <div
            className="w-full rounded-t-sm transition-all cursor-default"
            style={{ height: `${max ? (d[keyY] / max) * 100 : 2}%`, backgroundColor: color, opacity: 0.8 }}
          />
          <div className="absolute bottom-full mb-1 hidden group-hover:block bg-slate-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
            {d[keyX]}: {formatY ? formatY(d[keyY]) : d[keyY]}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function WeighingTickets() {
  const { authAxios, API, getToken } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState("");
  const [period, setPeriod] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [fraction, setFraction] = useState("");
  const [tickets, setTickets] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("list"); // "list" | "dashboard"
  const [selected, setSelected] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);

  // Available fractions, derived from tickets in view
  const fractions = Array.from(new Set(tickets.map(t => t.material_type).filter(Boolean))).sort();

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [lerCatalog, setLerCatalog] = useState([]);
  const [lerSummary, setLerSummary] = useState(null);
  const [lerMonth, setLerMonth] = useState(() => new Date().toISOString().slice(0, 7));

  // Fetch LER catalog once
  useEffect(() => {
    authAxios.get("/weighing-tickets/ler-catalog")
      .then(r => setLerCatalog(r.data.catalog || []))
      .catch(() => setLerCatalog([]));
  }, [authAxios]);

  // Fetch monthly LER summary when dashboard view + month + contract change
  useEffect(() => {
    if (view !== "dashboard") return;
    const params = { month: lerMonth };
    if (contractId) params.contract_id = contractId;
    authAxios.get("/weighing-tickets/monthly-ler-summary", { params })
      .then(r => setLerSummary(r.data))
      .catch(() => setLerSummary(null));
  }, [view, lerMonth, contractId, authAxios]);
  const fileRef = useRef();
  const pollRef = useRef();

  useEffect(() => {
    authAxios.get("/contracts").then(r => setContracts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadData();
  }, [contractId, period, customFrom, customTo]);

  function getDates() {
    if (period === "custom") return { from: customFrom, to: customTo };
    return getPeriodDates(period);
  }

  async function loadData() {
    const { from, to } = getDates();
    if (!from || !to) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: from, date_to: to });
      if (contractId) params.append("contract_id", contractId);
      const [tickRes, dashRes] = await Promise.all([
        authAxios.get(`/weighing-tickets?${params}`),
        authAxios.get(`/weighing-tickets/dashboard?${params}`),
      ]);
      setTickets(tickRes.data);
      setDashboard(dashRes.data);
    } catch {
      toast.error("Error cargando tickets de pesaje");
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(fileList) {
    if (!fileList || fileList.length === 0) return;
    if (!contractId) { toast.error("Selecciona un contrato antes de subir"); return; }
    const pdfs = Array.from(fileList).filter(f => f.name.toLowerCase().endsWith(".pdf"));
    if (pdfs.length === 0) { toast.error("Solo se admiten archivos PDF"); return; }

    setUploading(true);
    setBatchStatus(null);
    const fd = new FormData();
    fd.append("contract_id", contractId);
    pdfs.forEach(f => fd.append("files", f));
    try {
      const res = await authAxios.post("/weighing-tickets/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setBatchId(res.data.batch_id);
      toast.success(`Procesando ${res.data.total_files} archivo(s) con IA...`);
      pollBatch(res.data.batch_id);
    } catch {
      toast.error("Error al subir archivos");
      setUploading(false);
    }
  }

  function pollBatch(id) {
    pollRef.current = setInterval(async () => {
      try {
        const r = await authAxios.get(`/weighing-tickets/batch/${id}`);
        setBatchStatus(r.data);
        if (["completed", "partial", "failed"].includes(r.data.status)) {
          clearInterval(pollRef.current);
          setUploading(false);
          if (r.data.status !== "failed") {
            toast.success(`Extracción completada: ${r.data.processed} ticket(s) importados`);
            loadData();
          } else {
            toast.error("Error en la extracción");
          }
        }
      } catch { clearInterval(pollRef.current); setUploading(false); }
    }, 3000);
  }

  useEffect(() => () => clearInterval(pollRef.current), []);

  function toggleSelect(id) {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  }
  function toggleAll() {
    setSelected(s => s.length === tickets.length ? [] : tickets.map(t => t.id));
  }

  async function handleDelete() {
    if (!selected.length) return;
    if (!window.confirm(`¿Eliminar ${selected.length} ticket(s) seleccionado(s)?`)) return;
    setDeleting(true);
    try {
      await authAxios.post("/weighing-tickets/bulk-delete", { ids: selected });
      setSelected([]);
      toast.success(`${selected.length} ticket(s) eliminado(s)`);
      loadData();
    } catch {
      toast.error("Error al eliminar");
    } finally {
      setDeleting(false);
    }
  }

  const { from: df, to: dt } = getDates();

  return (
    <div className="p-6 space-y-5 max-w-screen-xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#E6F4EA] rounded-xl flex items-center justify-center">
            <Scale size={20} className="text-[#007934]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Tickets de Pesaje</h1>
            <p className="text-sm text-slate-400">Extracción automática con Claude IA</p>
          </div>
        </div>
        <button data-testid="refresh-weighing-btn" onClick={loadData}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 border border-slate-200 px-3 py-2 rounded-xl hover:bg-slate-50 transition-all">
          <RefreshCw size={14} /> Actualizar
        </button>
      </div>

      {/* Filters */}
      <div className="card-white p-4 flex flex-wrap items-center gap-3 animate-fade-in-up">
        <select data-testid="weighing-contract-select"
          value={contractId} onChange={e => setContractId(e.target.value)}
          className="border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/30">
          <option value="">Todos los contratos</option>
          {contracts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
          {PERIOD_OPTIONS.map(p => (
            <button key={p.id} data-testid={`period-${p.id}`}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${period === p.id ? "bg-white text-[#007934] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              {p.label}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              className="border border-slate-200 rounded-xl px-3 py-2 text-sm" />
            <span className="text-slate-400">→</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              className="border border-slate-200 rounded-xl px-3 py-2 text-sm" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {fractions.length > 0 && (
            <select
              value={fraction}
              onChange={e => setFraction(e.target.value)}
              data-testid="fraction-filter"
              className="border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold bg-white"
            >
              <option value="">Todas las fracciones</option>
              {fractions.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          )}
          <button
            data-testid="export-xlsx-btn"
            onClick={() => {
              const { from, to } = getDates();
              const params = new URLSearchParams();
              if (contractId) params.append("contract_id", contractId);
              if (from) params.append("date_from", from);
              if (to) params.append("date_to", to);
              if (fraction) params.append("fraction", fraction);
              const url = `${API}/weighing-tickets-export?${params}`;
              fetch(url, { headers: { Authorization: `Bearer ${getToken()}` }})
                .then(r => r.blob())
                .then(blob => {
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = `Tickets_Pesaje_${new Date().toISOString().slice(0,10)}.xlsx`;
                  a.click();
                  URL.revokeObjectURL(a.href);
                })
                .catch(() => toast.error("Error al exportar"));
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-colors"
          >
            <Download size={13} /> Exportar Excel
          </button>
          <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
            <button data-testid="view-list-btn" onClick={() => setView("list")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${view === "list" ? "bg-white text-[#007934] shadow-sm" : "text-slate-500"}`}>
              <List size={13} className="inline mr-1" />Lista
            </button>
            <button data-testid="view-dashboard-btn" onClick={() => setView("dashboard")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${view === "dashboard" ? "bg-white text-[#007934] shadow-sm" : "text-slate-500"}`}>
              <BarChart3 size={13} className="inline mr-1" />Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Upload Zone */}
      <div
        data-testid="weighing-upload-zone"
        className={`border-2 border-dashed rounded-2xl p-6 transition-all cursor-pointer ${dragOver ? "border-[#007934] bg-[#E6F4EA]/30" : "border-slate-200 bg-slate-50/50 hover:border-[#007934]/40"}`}
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files); }}
      >
        <input ref={fileRef} type="file" multiple accept=".pdf" className="hidden"
          onChange={e => handleUpload(e.target.files)} />
        <div className="flex flex-col items-center gap-2 text-center">
          {uploading
            ? <Loader2 size={28} className="text-[#007934] animate-spin" />
            : <UploadCloud size={28} className="text-slate-400" />}
          <p className="font-semibold text-slate-700 text-sm">
            {uploading ? "Procesando con Claude IA..." : "Arrastra PDFs aquí o haz clic para seleccionar"}
          </p>
          <p className="text-xs text-slate-400">Múltiples archivos PDF admitidos · Extracción automática de todos los campos</p>
          {!contractId && <p className="text-xs text-amber-500 font-medium">Selecciona un contrato antes de subir</p>}
        </div>
        {/* Batch Progress */}
        {batchStatus && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <div className="flex justify-between text-xs text-slate-500 mb-1.5">
              <span>{batchStatus.processed} / {batchStatus.total} procesados</span>
              {batchStatus.errors > 0 && <span className="text-red-500">{batchStatus.errors} errores</span>}
            </div>
            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-2 bg-[#007934] rounded-full transition-all"
                style={{ width: `${batchStatus.total ? (batchStatus.processed / batchStatus.total) * 100 : 0}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* KPI Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-fade-in-up">
          <StatCard icon={Package} label="Tickets importados" value={dashboard.total_tickets} />
          <StatCard icon={Scale} label="Total kg neto" value={Math.round(dashboard.total_kg_neto)} unit="kg" color="#059669" />
          <StatCard icon={Truck} label="Matrículas activas" value={dashboard.by_matricula?.length} color="#0891B2" />
          <StatCard icon={TrendingUp} label="Tipos de material" value={dashboard.by_material?.length} color="#7C3AED" />
        </div>
      )}

      {/* Delete bar */}
      {selected.length > 0 && (
        <div className="card-white p-3 flex items-center gap-3 border border-red-200 bg-red-50/50 animate-fade-in-up">
          <span className="text-sm font-semibold text-red-700">{selected.length} seleccionado(s)</span>
          <button data-testid="delete-selected-btn" onClick={handleDelete} disabled={deleting}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-all disabled:opacity-60">
            {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            Eliminar selección
          </button>
          <button onClick={() => setSelected([])} className="text-slate-400 hover:text-slate-700 ml-auto">
            <X size={16} />
          </button>
        </div>
      )}

      {/* List View */}
      {view === "list" && (
        <div className="card-white overflow-hidden animate-fade-in-up">
          {loading
            ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#007934]" /></div>
            : tickets.length === 0
            ? (
              <div className="text-center py-12">
                <Scale size={36} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 font-medium">No hay tickets en el periodo seleccionado</p>
                <p className="text-slate-300 text-sm mt-1">Sube PDFs usando la zona de carga superior</p>
              </div>
            )
            : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="w-10 p-3">
                      <input type="checkbox" data-testid="select-all-checkbox"
                        checked={selected.length === tickets.length && tickets.length > 0}
                        onChange={toggleAll}
                        className="rounded accent-[#007934]" />
                    </th>
                    {["Fecha", "H. Entrada", "H. Salida", "Contrato", "Matrícula", "Material", "LER", "Servicio", "Peso Neto (kg)", "Empresa", "Entrada", ""].map(h => (
                      <th key={h} className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tickets
                    .filter(t => !fraction || t.material_type === fraction)
                    .map((t, i) => (
                    <tr key={t.id} data-testid={`weighing-row-${i}`}
                      className={`border-b border-slate-50 hover:bg-slate-50/70 transition-colors ${selected.includes(t.id) ? "bg-[#E6F4EA]/40" : ""}`}>
                      <td className="p-3">
                        <input type="checkbox" checked={selected.includes(t.id)} onChange={() => toggleSelect(t.id)}
                          className="rounded accent-[#007934]" />
                      </td>
                      <td className="p-3 font-mono text-slate-600">{t.date || "—"}</td>
                      <td className="p-3 font-mono text-slate-500 text-xs">{t.time || "—"}</td>
                      <td className="p-3 font-mono text-slate-500 text-xs">{t.hora_salida || "—"}</td>
                      <td className="p-3">
                        <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full text-slate-600">
                          {contracts.find(c => c.id === t.contract_id)?.name || t.contract_id}
                        </span>
                      </td>
                      <td className="p-3 font-semibold text-slate-800">{t.matricula || "—"}</td>
                      <td className="p-3">
                        <span className="text-xs bg-[#E6F4EA] text-[#007934] px-2 py-0.5 rounded-full font-medium">
                          {t.material_type || "—"}
                        </span>
                      </td>
                      <td className="p-3 font-mono text-xs text-slate-500" title="Código LER">
                        {t.ler_code || "—"}
                      </td>
                      <td className="p-3 text-slate-600 text-xs">{t.servicio_prestado || "—"}</td>
                      <td className="p-3 font-bold text-slate-800">
                        {t.peso_neto_kg != null ? t.peso_neto_kg.toLocaleString("es-ES") : "—"}
                      </td>
                      <td className="p-3 text-slate-500 text-xs max-w-[200px] truncate">{t.empresa || "—"}</td>
                      <td className="p-3 text-slate-400 text-xs">{t.numero_entrada || "—"}</td>
                      <td className="p-3 text-right">
                        <button
                          onClick={() => setEditingTicket(t)}
                          data-testid={`edit-ticket-${i}`}
                          title="Editar ticket"
                          className="text-slate-400 hover:text-[#007934] p-1.5 rounded transition-colors"
                        >
                          <Pencil size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      )}

      {/* Dashboard View */}
      {view === "dashboard" && dashboard && (
        <div className="space-y-4 animate-fade-in-up">
          {/* Anomalies */}
          {dashboard.anomalies?.length > 0 && (
            <div className="card-white p-5 border-l-4 border-amber-400">
              <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                <AlertCircle size={16} className="text-amber-500" /> Anomalías detectadas
              </h3>
              <div className="space-y-2">
                {dashboard.anomalies.slice(0, 5).map((a, i) => (
                  <div key={i} className="text-sm bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 flex gap-3">
                    <span className="font-semibold text-amber-700">{a.type === "peso_alto" ? "Peso inusualmente alto" : a.type}</span>
                    <span className="text-amber-600">{a.date} · {a.matricula} · {a.value?.toLocaleString("es-ES")} kg</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Monthly LER report — kg per LER code (RESA/RESY) */}
          <div className="card-white p-5" data-testid="ler-monthly-card">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <BarChart3 size={16} className="text-[#007934]" />
                Reporte mensual por LER
                <span className="text-[10px] font-normal text-slate-400 ml-1">para RESA/RESY</span>
              </h3>
              <div className="flex items-center gap-2">
                <input
                  type="month"
                  value={lerMonth}
                  onChange={(e) => setLerMonth(e.target.value)}
                  data-testid="ler-month-input"
                  className="text-xs px-3 py-1.5 border border-slate-300 rounded-lg font-mono focus:ring-2 focus:ring-[#007934]/20 focus:border-[#007934] outline-none"
                />
                {lerSummary && (
                  <span className="text-xs text-slate-500 font-medium">
                    {lerSummary.total_tickets} tickets · <strong className="text-[#007934]">{(lerSummary.total_kg || 0).toLocaleString("es-ES")} kg</strong>
                  </span>
                )}
              </div>
            </div>

            {!lerSummary || lerSummary.items.length === 0 ? (
              <p className="text-xs text-slate-400 py-6 text-center">Sin tickets procesados en el mes seleccionado.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-500 uppercase text-[10px] tracking-wide border-b border-slate-200">
                      <th className="text-left py-2 font-semibold">LER</th>
                      <th className="text-left py-2 font-semibold">Descripción</th>
                      <th className="text-left py-2 font-semibold">Categoría</th>
                      <th className="text-right py-2 font-semibold">Kg</th>
                      <th className="text-right py-2 font-semibold">Tickets</th>
                      <th className="text-right py-2 font-semibold">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lerSummary.items.slice(0, 15).map(it => (
                      <tr key={it.code} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 font-mono font-semibold text-slate-800">{it.code}</td>
                        <td className="py-2 text-slate-600">{it.description}</td>
                        <td className="py-2">
                          <span className="inline-block px-2 py-0.5 rounded-full bg-[#E6F4EA] text-[#007934] text-[10px] font-semibold">
                            {it.category}
                          </span>
                        </td>
                        <td className="py-2 text-right font-mono font-bold text-slate-800">{it.kg.toLocaleString("es-ES")}</td>
                        <td className="py-2 text-right text-slate-500">{it.tickets}</td>
                        <td className="py-2 text-right text-slate-500">{it.pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {lerSummary.items.length > 15 && (
                  <p className="text-[10px] text-slate-400 mt-2 italic">+ {lerSummary.items.length - 15} códigos LER adicionales no mostrados.</p>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* By Material */}
            <div className="card-white p-5">
              <h3 className="font-bold text-slate-800 mb-4">Distribución por material</h3>
              {dashboard.by_material?.length > 0
                ? dashboard.by_material.slice(0, 8).map((m, i) => {
                    const pct = dashboard.total_kg_neto ? Math.round(m.kg / dashboard.total_kg_neto * 100) : 0;
                    return (
                      <div key={i} className="mb-2">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-slate-600 font-medium truncate max-w-[180px]">{m.material}</span>
                          <span className="text-slate-400">{m.kg.toLocaleString("es-ES")} kg ({pct}%)</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full">
                          <div className="h-1.5 bg-[#007934] rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })
                : <p className="text-sm text-slate-400">Sin datos</p>}
            </div>
            {/* By Vehicle */}
            <div className="card-white p-5">
              <h3 className="font-bold text-slate-800 mb-4">Top matrículas</h3>
              {dashboard.by_matricula?.length > 0
                ? dashboard.by_matricula.slice(0, 8).map((v, i) => {
                    const max = dashboard.by_matricula[0]?.kg || 1;
                    return (
                      <div key={i} className="mb-2">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="font-mono font-bold text-slate-700">{v.matricula}</span>
                          <span className="text-slate-400">{v.kg.toLocaleString("es-ES")} kg · {v.count} viajes</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full">
                          <div className="h-1.5 bg-[#059669] rounded-full" style={{ width: `${Math.round(v.kg / max * 100)}%` }} />
                        </div>
                      </div>
                    );
                  })
                : <p className="text-sm text-slate-400">Sin datos</p>}
            </div>
            {/* Evolution */}
            <div className="card-white p-5 md:col-span-2">
              <h3 className="font-bold text-slate-800 mb-4">Evolución de kg neto por día</h3>
              <MiniBarChart
                data={dashboard.by_day}
                keyX="date" keyY="kg"
                color="#007934"
                formatY={v => `${v.toLocaleString("es-ES")} kg`}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editingTicket && (
        <EditTicketModal
          ticket={editingTicket}
          onClose={() => setEditingTicket(null)}
          onSaved={(updated) => {
            setTickets(ts => ts.map(t => t.id === updated.id ? updated : t));
            setEditingTicket(null);
            toast.success("Ticket actualizado");
          }}
          onDelete={(id) => {
            setTickets(ts => ts.filter(t => t.id !== id));
            setSelected(s => s.filter(x => x !== id));
            setEditingTicket(null);
            toast.success("Ticket eliminado");
          }}
          authAxios={authAxios}
          lerCatalog={lerCatalog}
        />
      )}
    </div>
  );
}

function EditTicketModal({ ticket, onClose, onSaved, onDelete, authAxios, lerCatalog = [] }) {
  const [form, setForm] = useState({
    date: ticket.date || "",
    time: ticket.time || "",
    hora_salida: ticket.hora_salida || "",
    matricula: ticket.matricula || "",
    matricula_remolque: ticket.matricula_remolque || "",
    material_type: ticket.material_type || "",
    ler_code: ticket.ler_code || "",
    servicio_prestado: ticket.servicio_prestado || "",
    peso_neto_kg: ticket.peso_neto_kg ?? "",
    peso_bruto_kg: ticket.peso_bruto_kg ?? "",
    peso_vehiculo_kg: ticket.peso_vehiculo_kg ?? "",
    centro: ticket.centro || "",
    empresa: ticket.empresa || "",
    conductor: ticket.conductor || "",
    conductor_dni: ticket.conductor_dni || "",
    numero_entrada: ticket.numero_entrada || "",
    observaciones: ticket.observaciones || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v === "" || v === null) return;
        if (["peso_neto_kg", "peso_bruto_kg", "peso_vehiculo_kg"].includes(k)) {
          const num = parseFloat(v);
          if (!isNaN(num)) payload[k] = num;
        } else {
          payload[k] = v;
        }
      });
      const r = await authAxios.put(`/weighing-tickets/${ticket.id}`, payload);
      onSaved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Eliminar este ticket de pesaje? No se puede deshacer.")) return;
    try {
      await authAxios.delete(`/weighing-tickets/${ticket.id}`);
      onDelete(ticket.id);
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const setField = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} data-testid="edit-ticket-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Editar ticket de pesaje</h2>
            <p className="text-xs text-slate-500">{ticket.document_name || ticket.id?.slice(0, 8)}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Fecha"><input type="date" value={form.date} onChange={setField("date")} className="form-input" data-testid="edit-date" /></Field>
          <Field label="Nº Entrada"><input value={form.numero_entrada} onChange={setField("numero_entrada")} className="form-input" data-testid="edit-num" /></Field>
          <Field label="Hora entrada"><input type="time" value={form.time} onChange={setField("time")} className="form-input" data-testid="edit-hora-entrada" /></Field>
          <Field label="Hora salida"><input type="time" value={form.hora_salida} onChange={setField("hora_salida")} className="form-input" data-testid="edit-hora-salida" /></Field>
          <Field label="Matrícula"><input value={form.matricula} onChange={setField("matricula")} className="form-input font-mono" data-testid="edit-matricula" /></Field>
          <Field label="Matrícula remolque"><input value={form.matricula_remolque} onChange={setField("matricula_remolque")} className="form-input font-mono" data-testid="edit-matricula-remolque" /></Field>
          <Field label="Material / Fracción"><input value={form.material_type} onChange={setField("material_type")} className="form-input" data-testid="edit-material" placeholder="ej: RESTO, ENVASES, PAPEL/CARTÓN..." /></Field>
          <Field label="Código LER">
            <input
              list="ler-catalog-list"
              value={form.ler_code}
              onChange={setField("ler_code")}
              className="form-input font-mono"
              data-testid="edit-ler"
              placeholder="ej: 150107, 200301"
            />
            <datalist id="ler-catalog-list">
              {lerCatalog.map(l => (
                <option key={l.code} value={l.code}>{l.description}</option>
              ))}
            </datalist>
          </Field>
          <Field label="Servicio prestado" full><input value={form.servicio_prestado} onChange={setField("servicio_prestado")} className="form-input" data-testid="edit-servicio" placeholder="ej: RECOGIDA SELECTIVA, BARRIDO MECÁNICO..." /></Field>
          <Field label="Peso neto (kg)"><input type="number" value={form.peso_neto_kg} onChange={setField("peso_neto_kg")} className="form-input" data-testid="edit-peso-neto" /></Field>
          <Field label="Peso bruto (kg)"><input type="number" value={form.peso_bruto_kg} onChange={setField("peso_bruto_kg")} className="form-input" /></Field>
          <Field label="Tara (kg)"><input type="number" value={form.peso_vehiculo_kg} onChange={setField("peso_vehiculo_kg")} className="form-input" /></Field>
          <Field label="Centro / Planta"><input value={form.centro} onChange={setField("centro")} className="form-input" /></Field>
          <Field label="Empresa"><input value={form.empresa} onChange={setField("empresa")} className="form-input" /></Field>
          <Field label="Conductor"><input value={form.conductor} onChange={setField("conductor")} className="form-input" data-testid="edit-conductor" /></Field>
          <Field label="DNI conductor"><input value={form.conductor_dni || ""} onChange={setField("conductor_dni")} className="form-input font-mono" data-testid="edit-conductor-dni" placeholder="00000000A" /></Field>
          <Field label="Observaciones" full><textarea rows={2} value={form.observaciones} onChange={setField("observaciones")} className="form-input" /></Field>
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <button onClick={handleDelete} className="inline-flex items-center gap-1.5 text-red-600 hover:text-red-700 text-sm font-semibold" data-testid="delete-ticket-btn">
            <Trash2 size={14} /> Eliminar
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900">Cancelar</button>
            <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-1.5 px-5 py-2 bg-[#007934] hover:bg-[#005c27] text-white rounded-lg text-sm font-bold disabled:opacity-60" data-testid="save-ticket-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Guardar
            </button>
          </div>
        </div>
      </div>
      <style>{`.form-input{width:100%;padding:8px 12px;border:1px solid #e2e8f0;border-radius:8px;font-size:13px;}.form-input:focus{outline:none;border-color:#007934;box-shadow:0 0 0 2px #00793420;}`}</style>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-[11px] font-bold text-slate-500 mb-1 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}
